/**
 * NeoCoreCreditoDetallePage — vista completa de un crédito.
 *
 * Mismo lenguaje visual que NeoCorePlazoFijoDetallePage. Sirve para que
 * el operador pueda darle seguimiento al crédito a lo largo de su vida:
 *   - Hero con capital, saldo, fechas y estado de provisión
 *   - Plan de cuotas (calendario con estado de cada cuota)
 *   - Historial de pagos
 *   - Ledger interno (credito_movimiento)
 *
 * Cuando esté operativo el motor de cobranza (#38), agregamos botón
 * "Registrar pago" + acciones de cobranza. Por ahora es solo vista.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CaretDown,
  CaretUp,
  CheckCircle,
  Coins,
  HandHeart,
  Hourglass,
  ListChecks,
  Printer,
  Receipt,
  Warning,
  XCircle,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { RegistrarPagoCreditoDrawer } from '@/components/RegistrarPagoCreditoDrawer'
import { imprimirPagareCredito } from '@/lib/imprimirPagareCredito'

// =========================================================
// Tipos
// =========================================================

type Credito = {
  id: string
  solicitud_credito_id: string
  asociado_id: string
  tipo_credito_id: string
  capital_inicial: number
  tasa: number
  periodicidad_tasa: string
  plazo_dias: number
  metodo_cuota: string
  total_cuotas: number
  fecha_desembolso: string
  fecha_primera_cuota: string
  fecha_ultima_cuota: string
  estado: string
  estado_provision: string | null
  saldo_capital: number
  intereses_devengados: number
  intereses_cobrados: number
  mora_acumulada: number
  cuenta_ahorro_destino_id: string | null
  verification_code: string
  created_at: string
}

type Cuota = {
  id: string
  numero: number
  fecha_vencimiento: string
  capital: number
  interes: number
  total: number
  capital_pagado: number
  interes_pagado: number
  mora_acumulada: number
  mora_pagada: number
  estado: string
  pagada_at: string | null
  dias_atraso_actual: number
}

type Pago = {
  id: string
  credito_cuota_id: string
  monto_total: number
  aplicado_capital: number
  aplicado_interes: number
  aplicado_mora: number
  fecha_pago: string
  notas: string | null
}

type Movimiento = {
  id: string
  tipo: string
  monto: number
  saldo_capital_post: number | null
  descripcion: string | null
  fecha: string
}

type Asociado = {
  id: string
  no_asociado: string
  nombre_completo: string
  dpi: string
}

type TipoCredito = {
  codigo: string
  nombre: string
}

// =========================================================
// Helpers
// =========================================================

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

const fmtFechaHora = (iso: string) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

// Tokens de estado — usamos var() con fallback para que el sistema de
// design pueda overridear desde global.css. Verde = ok, ámbar = warn,
// rojo = danger, slate = neutral cerrado.
const ESTADO_CREDITO_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  vigente:               { label: 'Vigente',                color: 'var(--ok-ink, #0f8a57)',     bg: 'rgba(15,138,87,0.08)' },
  al_dia:                { label: 'Al día',                 color: 'var(--ok-ink, #0f8a57)',     bg: 'rgba(15,138,87,0.08)' },
  en_mora:               { label: 'En mora',                color: 'var(--warn-ink, #b8860b)',   bg: 'rgba(184,134,11,0.10)' },
  cancelado:             { label: 'Cancelado',              color: 'var(--ink-soft, #4b5563)',   bg: 'rgba(75,85,99,0.08)' },
  cancelado_anticipado:  { label: 'Pre-cancelado',          color: 'var(--ink-soft, #4b5563)',   bg: 'rgba(75,85,99,0.08)' },
  castigado:             { label: 'Castigado',              color: 'var(--danger, #a32d2d)',     bg: 'rgba(163,45,45,0.08)' },
}

const ESTADO_CUOTA_LABEL: Record<string, { label: string; color: string }> = {
  pendiente:      { label: 'Pendiente',      color: 'var(--ink-soft, #4b5563)' },
  pagada:         { label: 'Pagada',         color: 'var(--ok-ink, #0f8a57)' },
  pagada_parcial: { label: 'Parcial',        color: 'var(--warn-ink, #b8860b)' },
  vencida:        { label: 'Vencida',        color: 'var(--warn-ink, #b8860b)' },
  mora:           { label: 'En mora',        color: 'var(--danger, #a32d2d)' },
  castigada:      { label: 'Castigada',      color: 'var(--danger, #a32d2d)' },
}

const TIPO_MOV_LABEL: Record<string, string> = {
  desembolso:         'Desembolso',
  devenga_interes:    'Devengo de interés',
  devenga_mora:       'Devengo de mora',
  cobro_capital:      'Cobro de capital',
  cobro_interes:      'Cobro de interés',
  cobro_mora:         'Cobro de mora',
  provision_aplicada: 'Provisión aplicada',
  castigo:            'Castigo',
  reversa:            'Reversa',
}

// =========================================================
// Componente
// =========================================================

export function NeoCoreCreditoDetallePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [credito, setCredito] = useState<Credito | null>(null)
  const [asociado, setAsociado] = useState<Asociado | null>(null)
  const [tipo, setTipo] = useState<TipoCredito | null>(null)
  const [cuotas, setCuotas] = useState<Cuota[]>([])
  const [pagos, setPagos] = useState<Pago[]>([])
  const [movs, setMovs] = useState<Movimiento[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagoOpen, setPagoOpen] = useState(false)
  const [moraOpen, setMoraOpen] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      // Crédito + asociado + tipo
      const { data: c, error: cErr } = await supabase
        .from('credito')
        .select(`
          *,
          asociado:asociado_id (id, no_asociado, nombre_completo, dpi),
          tipo:tipo_credito_id (codigo, nombre)
        `)
        .eq('id', id)
        .single()
      if (cErr) throw cErr
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cr = c as any
      setCredito(cr as Credito)
      setAsociado(cr.asociado as Asociado)
      setTipo(cr.tipo as TipoCredito)

      // Cuotas + pagos + movimientos en paralelo
      const [{ data: cuoData }, { data: pagosData }, { data: movsData }] = await Promise.all([
        supabase.from('credito_cuota').select('*').eq('credito_id', id).order('numero', { ascending: true }),
        supabase.from('credito_pago').select('*').eq('credito_id', id).order('fecha_pago', { ascending: false }),
        supabase.from('credito_movimiento').select('*').eq('credito_id', id).order('fecha', { ascending: false }),
      ])
      setCuotas((cuoData ?? []) as Cuota[])
      setPagos((pagosData ?? []) as Pago[])
      setMovs((movsData ?? []) as Movimiento[])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (loading) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted-foreground)' }}>
        Cargando crédito…
      </div>
    )
  }
  if (error || !credito || !asociado || !tipo) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Warning size={36} color="var(--danger, #a32d2d)" weight="duotone" />
        <p style={{ color: 'var(--danger, #a32d2d)' }}>{error ?? 'Crédito no encontrado'}</p>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>Volver</button>
      </div>
    )
  }

  const estadoBadge = ESTADO_CREDITO_LABEL[credito.estado] ?? {
    label: credito.estado, color: 'var(--ink-soft, #4b5563)', bg: 'rgba(75,85,99,0.08)',
  }
  const totalPagado = pagos.reduce((acc, p) => acc + p.monto_total, 0)
  const totalACobrar = cuotas.reduce((acc, c) => acc + c.total, 0)
  const pendiente = totalACobrar - cuotas.reduce((acc, c) => acc + c.capital_pagado + c.interes_pagado, 0)
  const tienePendientes = cuotas.some((c) => c.estado !== 'pagada' && c.estado !== 'castigada')
  const puedePagar = tienePendientes && credito.estado !== 'cancelado' && credito.estado !== 'castigado' && credito.estado !== 'cancelado_anticipado'
  const moraPendiente = cuotas.reduce((a, c) => a + Math.max(0, c.mora_acumulada - c.mora_pagada), 0)

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header con back + asociado + acciones */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px' }}
        >
          <ArrowLeft size={14} weight="bold" /> Volver
        </button>
        <Link
          to={`/neocore/asociados/${asociado.id}`}
          style={{ fontSize: 12, color: 'var(--muted-foreground)', textDecoration: 'none' }}
        >
          {asociado.nombre_completo} · {asociado.no_asociado} →
        </Link>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-ghost"
          onClick={async () => {
            const r = await imprimirPagareCredito(credito.id)
            if (!r.ok) alert('No pudimos imprimir el pagaré: ' + r.error)
          }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Printer size={14} weight="bold" /> Pagaré
        </button>
        {moraPendiente > 0 && (
          <button
            className="btn btn-ghost"
            onClick={() => setMoraOpen(true)}
            title="Condonar la mora (total o parcial)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--danger, #a32d2d)', borderColor: 'rgba(163,45,45,0.35)' }}
          >
            <HandHeart size={14} weight="bold" /> Gestionar mora · {fmtQ(moraPendiente)}
          </button>
        )}
        {puedePagar && (
          <button
            className="btn btn-primary"
            onClick={() => setPagoOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Receipt size={14} weight="bold" /> Registrar pago
          </button>
        )}
      </div>

      {/* Hero */}
      <div style={{
        background: 'var(--surface)', borderRadius: 16, padding: '20px 24px',
        border: '1px solid var(--hairline)', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: 'var(--brand-soft, #ffe4d4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--brand-deep, #c43c00)',
          }}>
            <Coins size={28} weight="duotone" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
              {tipo.nombre}
            </p>
            <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800 }}>
              Crédito {credito.id.slice(0, 8)}
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              Desembolsado el {fmtFecha(credito.fecha_desembolso)}
            </p>
          </div>
          <span style={{
            background: estadoBadge.bg, color: estadoBadge.color, padding: '6px 12px',
            borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 1, whiteSpace: 'nowrap',
          }}>
            {estadoBadge.label}
            {credito.estado_provision && ` · ${credito.estado_provision}`}
          </span>
        </div>

        {/* Stats grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12, marginTop: 20,
        }}>
          <Stat label="Capital" value={fmtQ(credito.capital_inicial)} />
          <Stat label="Saldo pendiente" value={fmtQ(pendiente)} accent />
          <Stat label="Tasa" value={(credito.tasa * 100).toFixed(2) + '% ' + credito.periodicidad_tasa} />
          <Stat label="Cuotas" value={credito.total_cuotas + ' · ' + credito.plazo_dias + 'd'} />
          <Stat label="Vence" value={fmtFecha(credito.fecha_ultima_cuota)} />
          <Stat label="Cobrado" value={fmtQ(totalPagado)} />
        </div>
      </div>

      {/* Próxima cuota destacada con CTA — solo si quedan pendientes */}
      {puedePagar && (() => {
        const prox = cuotas.find((c) => c.estado !== 'pagada' && c.estado !== 'castigada')
        if (!prox) return null
        const pendienteCuota =
          (prox.capital - prox.capital_pagado) +
          (prox.interes - prox.interes_pagado) +
          (prox.mora_acumulada - prox.mora_pagada)
        return (
          <div style={{
            background: 'var(--brand-soft, #ffe4d4)',
            border: '1px solid rgba(196,60,0,0.20)',
            borderRadius: 16, padding: '16px 20px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{ flex: 1 }}>
              <p style={{
                margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                color: 'var(--brand-deep, #c43c00)', textTransform: 'uppercase',
              }}>
                Próxima cuota · {prox.numero} de {credito.total_cuotas}
              </p>
              <p style={{
                margin: '4px 0 0', fontSize: 26, fontWeight: 800,
                fontFeatureSettings: '"tnum"', color: 'var(--ink)',
              }}>
                {fmtQ(pendienteCuota)}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--brand-deep)' }}>
                Vence {fmtFecha(prox.fecha_vencimiento)}
                {prox.dias_atraso_actual > 0 && ` · ${prox.dias_atraso_actual} días de atraso`}
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setPagoOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}
            >
              <Receipt size={14} weight="bold" /> Cobrar cuota
            </button>
          </div>
        )
      })()}

      {/* Plan de cuotas */}
      <Section title="Plan de cuotas" Icon={ListChecks}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
              <ThCol>#</ThCol>
              <ThCol>Vence</ThCol>
              <ThCol align="right">Capital</ThCol>
              <ThCol align="right">Interés</ThCol>
              <ThCol align="right">Mora</ThCol>
              <ThCol align="right">Total</ThCol>
              <ThCol align="right">Pagado</ThCol>
              <ThCol>Estado</ThCol>
            </tr>
          </thead>
          <tbody>
            {cuotas.map((c) => {
              const e = ESTADO_CUOTA_LABEL[c.estado] ?? { label: c.estado, color: 'var(--ink-soft, #4b5563)' }
              const totalPagadoCuota = c.capital_pagado + c.interes_pagado + c.mora_pagada
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <TdCol>{c.numero}</TdCol>
                  <TdCol>
                    <div>{fmtFecha(c.fecha_vencimiento)}</div>
                    {c.dias_atraso_actual > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--danger, #a32d2d)' }}>
                        +{c.dias_atraso_actual} días atraso
                      </div>
                    )}
                  </TdCol>
                  <TdCol align="right" mono>{fmtQ(c.capital)}</TdCol>
                  <TdCol align="right" mono color="var(--brand-deep)">{fmtQ(c.interes)}</TdCol>
                  <TdCol align="right" mono color={c.mora_acumulada > 0 ? 'var(--danger)' : 'var(--muted-foreground)'}>
                    {fmtQ(c.mora_acumulada)}
                  </TdCol>
                  <TdCol align="right" mono bold>{fmtQ(c.total + c.mora_acumulada)}</TdCol>
                  <TdCol align="right" mono>{fmtQ(totalPagadoCuota)}</TdCol>
                  <TdCol>
                    <span style={{ color: e.color, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {e.label}
                    </span>
                  </TdCol>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Section>

      {/* Pagos recibidos */}
      <Section title={`Pagos recibidos · ${pagos.length}`} Icon={Receipt}>
        {pagos.length === 0 ? (
          <Empty Icon={Hourglass} text="Aún no se ha registrado ningún pago." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                <ThCol>Fecha</ThCol>
                <ThCol>Cuota</ThCol>
                <ThCol align="right">Capital</ThCol>
                <ThCol align="right">Interés</ThCol>
                <ThCol align="right">Mora</ThCol>
                <ThCol align="right">Total</ThCol>
              </tr>
            </thead>
            <tbody>
              {pagos.map((p) => {
                const cuota = cuotas.find((c) => c.id === p.credito_cuota_id)
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <TdCol>{fmtFechaHora(p.fecha_pago)}</TdCol>
                    <TdCol>{cuota ? `#${cuota.numero}` : '—'}</TdCol>
                    <TdCol align="right" mono>{fmtQ(p.aplicado_capital)}</TdCol>
                    <TdCol align="right" mono color="var(--brand-deep)">{fmtQ(p.aplicado_interes)}</TdCol>
                    <TdCol align="right" mono color={p.aplicado_mora > 0 ? 'var(--danger)' : 'var(--muted-foreground)'}>
                      {fmtQ(p.aplicado_mora)}
                    </TdCol>
                    <TdCol align="right" mono bold>{fmtQ(p.monto_total)}</TdCol>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Section>

      {/* Modal de gestión de mora (condonación) */}
      {moraOpen && (
        <CondonarMoraModal
          creditoId={credito.id}
          asociadoNombre={asociado.nombre_completo}
          noAsociado={asociado.no_asociado}
          moraPendiente={moraPendiente}
          onClose={() => setMoraOpen(false)}
          onDone={() => { setMoraOpen(false); void load() }}
        />
      )}

      {/* Drawer de registro de pago */}
      <RegistrarPagoCreditoDrawer
        open={pagoOpen}
        onClose={() => { setPagoOpen(false); void load() }}
        creditoId={credito.id}
        asociadoNombre={asociado.nombre_completo}
        // load inmediato → cuando el user cierre el drawer, la página
        // detrás ya está actualizada con la cuota pagada
        onSuccess={() => { void load() }}
      />

      {/* Ledger interno */}
      <Section title={`Movimientos internos · ${movs.length}`} Icon={CheckCircle} collapsedByDefault>
        {movs.length === 0 ? (
          <Empty Icon={Hourglass} text="Sin movimientos." />
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {movs.map((m) => (
              <li key={m.id} style={{
                padding: '10px 0', borderBottom: '1px solid var(--line-soft)',
                display: 'flex', gap: 12, alignItems: 'baseline',
              }}>
                <span style={{ fontSize: 11, color: 'var(--muted-foreground)', minWidth: 130, fontFamily: 'monospace' }}>
                  {fmtFechaHora(m.fecha)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {TIPO_MOV_LABEL[m.tipo] ?? m.tipo}
                  </div>
                  {m.descripcion && (
                    <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{m.descripcion}</div>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>
                  {fmtQ(m.monto)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

// =========================================================
// Subcomponentes
// =========================================================

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p style={{
        margin: 0, fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase',
        letterSpacing: 1.5, fontWeight: 700,
      }}>
        {label}
      </p>
      <p style={{
        margin: '4px 0 0', fontSize: 18, fontWeight: 800,
        color: accent ? 'var(--brand)' : 'var(--ink)',
        fontFeatureSettings: '"tnum"',
      }}>
        {value}
      </p>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Section({ title, Icon, children, collapsedByDefault }: { title: string; Icon: any; children: React.ReactNode; collapsedByDefault?: boolean }) {
  const [open, setOpen] = useState(!collapsedByDefault)
  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--hairline)',
      marginBottom: 14, overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', padding: '14px 20px', background: 'transparent', border: 0,
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left',
        }}
      >
        <Icon size={18} weight="duotone" color="var(--ink-soft, #4b5563)" />
        <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{title}</span>
        {open
          ? <CaretUp size={14} weight="bold" color="var(--muted-foreground)" />
          : <CaretDown size={14} weight="bold" color="var(--muted-foreground)" />}
      </button>
      {open && <div style={{ padding: '0 20px 18px' }}>{children}</div>}
    </div>
  )
}

function ThCol({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th style={{
      textAlign: align ?? 'left', padding: '8px 6px',
      fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)',
      textTransform: 'uppercase', letterSpacing: 1,
    }}>
      {children}
    </th>
  )
}

function TdCol({
  children, align, mono, bold, color,
}: {
  children: React.ReactNode; align?: 'right'; mono?: boolean; bold?: boolean; color?: string
}) {
  return (
    <td style={{
      padding: '10px 6px', textAlign: align ?? 'left',
      fontFeatureSettings: mono ? '"tnum"' : undefined,
      fontWeight: bold ? 700 : undefined,
      color: color ?? undefined,
    }}>
      {children}
    </td>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Empty({ Icon, text }: { Icon: any; text: string }) {
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted-foreground)' }}>
      <Icon size={28} weight="duotone" />
      <p style={{ fontSize: 13, margin: '8px 0 0' }}>{text}</p>
    </div>
  )
}

// =========================================================
// Modal · condonar mora (100% / % / monto) con motivo obligatorio
// =========================================================
function CondonarMoraModal({
  creditoId, asociadoNombre, noAsociado, moraPendiente, onClose, onDone,
}: {
  creditoId: string; asociadoNombre: string; noAsociado: string
  moraPendiente: number; onClose: () => void; onDone: () => void
}) {
  const [modo, setModo] = useState<'total' | 'parcial'>('total')
  const [tipoParcial, setTipoParcial] = useState<'pct' | 'monto'>('monto')
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)

  const pctRaw = modo === 'total'
    ? 100
    : (Number(valor) > 0
        ? (tipoParcial === 'pct' ? Math.min(100, Number(valor)) : Math.min(100, (Number(valor) / moraPendiente) * 100))
        : 0)
  const montoAplicar = Math.round((moraPendiente * pctRaw) / 100 * 100) / 100

  const confirmar = async () => {
    if (!motivo.trim()) { alert('El motivo es obligatorio (queda en el resguardo).'); return }
    if (pctRaw <= 0) { alert('Ingresá un monto o porcentaje válido.'); return }
    setBusy(true)
    const { data, error } = await supabase.rpc('condonar_mora_credito', {
      p_credito_id: creditoId, p_pct: pctRaw, p_motivo: motivo.trim(),
    })
    setBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    const res = data as { condonado_total?: number } | null
    alert(`Mora condonada: ${fmtQ(res?.condonado_total ?? montoAplicar)}. Queda registrado.`)
    onDone()
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.55)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 100%)', background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.24)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--hairline)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Gestionar mora</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>{asociadoNombre} · {noAsociado}</p>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 12px', background: 'rgba(163,45,45,0.05)', border: '1px solid rgba(163,45,45,0.18)', borderRadius: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 700 }}>Mora pendiente</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--danger, #a32d2d)' }}>{fmtQ(moraPendiente)}</span>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {(['total', 'parcial'] as const).map((m) => (
              <button key={m} onClick={() => setModo(m)} style={{
                all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 8,
                fontSize: 13, fontWeight: 700, border: '1px solid',
                borderColor: modo === m ? 'var(--brand)' : 'var(--hairline)',
                background: modo === m ? 'var(--brand-50)' : 'transparent',
                color: modo === m ? 'var(--brand-700)' : 'var(--ink-soft)',
              }}>{m === 'total' ? 'Condonar todo' : 'Parcial'}</button>
            ))}
          </div>

          {modo === 'parcial' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['monto', 'pct'] as const).map((t) => (
                  <button key={t} onClick={() => setTipoParcial(t)} style={{
                    all: 'unset', cursor: 'pointer', padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                    background: tipoParcial === t ? 'var(--brand)' : 'var(--n-100)', color: tipoParcial === t ? '#fff' : 'var(--muted-foreground)',
                  }}>{t === 'monto' ? 'Por monto (Q)' : 'Por %'}</button>
                ))}
              </div>
              <input type="number" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)}
                placeholder={tipoParcial === 'monto' ? 'Monto a condonar (Q)' : 'Porcentaje (%)'}
                style={{ padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13 }} />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
                Se condonará <strong>{fmtQ(montoAplicar)}</strong> · quedará pendiente {fmtQ(Math.max(0, moraPendiente - montoAplicar))}
              </p>
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
            Motivo (obligatorio · queda en el resguardo contable)
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
              placeholder="Ej. Asociado con intención de pago; se apoya con la mora del atraso por…"
              style={{ padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
          </label>

          <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
            No borra el atraso ni toca capital/interés. El asociado sigue en mora hasta que pague la cuota; solo se le quita el recargo.
          </p>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmar} style={{ flex: 1 }} disabled={busy || !motivo.trim() || pctRaw <= 0}>
            {busy ? 'Condonando…' : `Condonar ${fmtQ(montoAplicar)}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// Importación que necesita el Icon de XCircle/Warning si fuera necesario en algún empty extra
// (Ya importados arriba). XCircle se usa indirectamente — evitamos warning unused:
void XCircle
